import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { financeiroService } from '../services/financeiroService';
import type { SalvarOrcamentoRequest, SalvarDespesaRequest, SalvarMedicaoRequest } from '../types';

const KEYS = {
  orcamento: (p: number) => ['projeto', p, 'orcamento'] as const,
  despesas: (p: number) => ['projeto', p, 'despesas'] as const,
  painel: (p: number) => ['projeto', p, 'orcado-realizado'] as const,
  medicoes: (p: number) => ['projeto', p, 'medicoes'] as const,
};

export function useMedicoes(projetoId: number) {
  return useQuery({ queryKey: KEYS.medicoes(projetoId), queryFn: () => financeiroService.listarMedicoes(projetoId) });
}

export function useMedicaoMutations(projetoId: number) {
  const qc = useQueryClient();
  const inval = () => {
    qc.invalidateQueries({ queryKey: KEYS.medicoes(projetoId) });
    qc.invalidateQueries({ queryKey: KEYS.painel(projetoId) });
  };
  const salvar = useMutation({
    mutationFn: ({ medId, req }: { medId?: number; req: SalvarMedicaoRequest }) =>
      medId ? financeiroService.atualizarMedicao(projetoId, medId, req)
            : financeiroService.criarMedicao(projetoId, req),
    onSuccess: inval,
  });
  const excluir = useMutation({
    mutationFn: (medId: number) => financeiroService.excluirMedicao(projetoId, medId),
    onSuccess: inval,
  });
  const faturar = useMutation({
    mutationFn: ({ medId, dtVencimento }: { medId: number; dtVencimento: string }) =>
      financeiroService.faturarMedicao(projetoId, medId, dtVencimento),
    onSuccess: inval,
  });
  const cancelar = useMutation({
    mutationFn: (medId: number) => financeiroService.cancelarMedicao(projetoId, medId),
    onSuccess: inval,
  });
  return { salvar, excluir, faturar, cancelar };
}

export function useOrcamento(projetoId: number) {
  return useQuery({ queryKey: KEYS.orcamento(projetoId), queryFn: () => financeiroService.listarOrcamento(projetoId) });
}
export function useDespesas(projetoId: number) {
  return useQuery({ queryKey: KEYS.despesas(projetoId), queryFn: () => financeiroService.listarDespesas(projetoId) });
}
export function useOrcadoRealizado(projetoId: number) {
  return useQuery({ queryKey: KEYS.painel(projetoId), queryFn: () => financeiroService.orcadoRealizado(projetoId) });
}

export function useFinanceiroMutations(projetoId: number) {
  const qc = useQueryClient();
  const inval = () => {
    qc.invalidateQueries({ queryKey: KEYS.orcamento(projetoId) });
    qc.invalidateQueries({ queryKey: KEYS.despesas(projetoId) });
    qc.invalidateQueries({ queryKey: KEYS.painel(projetoId) });
  };

  const salvarOrcamento = useMutation({
    mutationFn: (req: SalvarOrcamentoRequest) => financeiroService.salvarOrcamento(projetoId, req),
    onSuccess: inval,
  });
  const salvarDespesa = useMutation({
    mutationFn: ({ despId, req }: { despId?: number; req: SalvarDespesaRequest }) =>
      despId ? financeiroService.atualizarDespesa(projetoId, despId, req)
             : financeiroService.criarDespesa(projetoId, req),
    onSuccess: inval,
  });
  const excluirDespesa = useMutation({
    mutationFn: (despId: number) => financeiroService.excluirDespesa(projetoId, despId),
    onSuccess: inval,
  });

  return { salvarOrcamento, salvarDespesa, excluirDespesa };
}
